import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../components/layout/Layout";
import EmployeePicker from "../../components/ui/EmployeePicker";
import {
  useEmployeeBalances,
  useFileLeave,
  useLeaveTypes,
} from "../../api/queries/leaveQueries";
import useCurrentUser from "../../hooks/useCurrentUser";
import { getApiErrorMessage } from "../../utils/apiError";
import { formatPlainDate, todayInManila } from "../../utils/dateTime";
import { employeeName } from "../../utils/text";
import {
  daysInclusive,
  formatDays,
  formatLeaveDates,
  num,
} from "./components/review/leaveFormat";
import "./leave.css";

const REASON_MAX = 500;
const EMPTY_FORM = {
  leaveTypeId: "",
  startDate: "",
  endDate: "",
  halfDay: false,
  totalDays: "",
  reason: "",
};

/**
 * HR / approvers file a leave for an employee in their scope.
 * Same rules as the employee portal; POST /leave/request with employeeId.
 */
const FileLeavePage = () => {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const [employee, setEmployee] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState([]);
  const fileLeave = useFileLeave();

  // Balances of the anniversary cycle that contains the start date (today until a date is picked);
  // the leave is charged to that cycle
  const asOf = form.startDate || todayInManila();
  const balancesQuery = useEmployeeBalances(employee?.id, asOf);
  const typesQuery = useLeaveTypes();

  const typeName = useMemo(() => {
    const map = new Map((typesQuery.data || []).map((t) => [t.id, t.name]));
    return (b) => b.leaveType?.name || map.get(b.leaveTypeId) || "Leave";
  }, [typesQuery.data]);

  const balances = balancesQuery.data || [];
  const selectedBalance = balances.find(
    (b) => b.leaveTypeId === form.leaveTypeId,
  );
  const available = selectedBalance ? num(selectedBalance.available) : null;

  const calendarDays = daysInclusive(form.startDate, form.endDate);
  const isSingleDay = calendarDays === 1;
  const totalDays =
    form.totalDays === ""
      ? form.halfDay
        ? 0.5
        : calendarDays
      : Number(form.totalDays);
  const overBalance = available !== null && totalDays > available;

  const set = (changes) => setForm((f) => ({ ...f, ...changes }));
  // Changing dates resets the day count to the calculated value
  const setDates = (changes) => {
    const next = { ...form, ...changes };
    if (next.startDate && (!next.endDate || next.endDate < next.startDate))
      next.endDate = next.startDate;
    if (daysInclusive(next.startDate, next.endDate) !== 1) next.halfDay = false;
    setForm({ ...next, totalDays: "" });
  };

  const pickEmployee = (emp) => {
    setEmployee(emp);
    setForm(EMPTY_FORM);
    setErrors([]);
  };

  const validate = () => {
    const list = [];
    if (!employee) list.push("Choose an employee.");
    if (!form.leaveTypeId) list.push("Choose a leave type.");
    if (!form.startDate || !form.endDate)
      list.push("Pick the start and end dates.");
    else if (form.endDate < form.startDate)
      list.push("End date can't be before the start date.");
    if (!(totalDays > 0) || (totalDays * 2) % 1 !== 0)
      list.push("Total days must be in steps of 0.5.");
    else if (calendarDays && totalDays > calendarDays)
      list.push(
        `Total days can't be more than ${calendarDays} for these dates.`,
      );
    if (!form.reason.trim()) list.push("Enter a reason.");
    return list;
  };

  const submit = (e) => {
    e.preventDefault();
    const list = validate();
    setErrors(list);
    if (list.length) return;
    fileLeave.mutate(
      {
        employeeId: employee.id,
        leaveTypeId: form.leaveTypeId,
        startDate: form.startDate,
        endDate: form.endDate,
        totalDays,
        reason: form.reason.trim(),
      },
      {
        onSuccess: () => {
          toast.success(`Leave filed for ${employeeName(employee)}.`);
          navigate(
            `/leave/approvals?search=${encodeURIComponent(employee.employeeId || "")}`,
          );
        },
        onError: (err) =>
          setErrors([getApiErrorMessage(err, "Could not file the leave.")]),
      },
    );
  };

  const busy = fileLeave.isPending;

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0 page-title">File Leave</h1>
            <p className="text-muted small mb-0">
              File a leave on behalf of an employee in your scope.
            </p>
          </div>
          <Link
            to="/leave/approvals"
            className="btn profile-btn profile-btn-white"
          >
            <i className="bi bi-arrow-left"></i> Leave Approvals
          </Link>
        </div>

        <form className="leave-file-layout" onSubmit={submit} noValidate>
          <div className="leave-card">
            {errors.length > 0 && (
              <div className="alert alert-danger fs-085 py-2" role="alert">
                {errors.length === 1 ? (
                  errors[0]
                ) : (
                  <ul className="mb-0 ps-3">
                    {errors.map((m) => (
                      <li key={m}>{m}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <h6 className="leave-section-title">1. Employee</h6>
            <EmployeePicker
              companyId={user.companyId}
              value={employee}
              onChange={pickEmployee}
              disabled={busy}
              autoFocus
            />

            <fieldset disabled={!employee || busy} className="mt-4">
              <h6 className="leave-section-title">2. Leave details</h6>
              <div className="row g-3">
                <div className="col-12">
                  <label htmlFor="file-type" className="profile-label">
                    Leave type <span className="text-danger">*</span>
                  </label>
                  <select
                    id="file-type"
                    className="form-select"
                    value={form.leaveTypeId}
                    onChange={(e) => set({ leaveTypeId: e.target.value })}
                  >
                    <option value="">
                      {!employee
                        ? "Choose an employee first"
                        : balancesQuery.isLoading
                          ? "Loading balances…"
                          : "Select leave type"}
                    </option>
                    {balances.map((b) => (
                      <option key={b.leaveTypeId} value={b.leaveTypeId}>
                        {typeName(b)} — {num(b.available)} available
                      </option>
                    ))}
                  </select>
                  {employee &&
                    !balancesQuery.isLoading &&
                    balances.length === 0 && (
                      <div className="text-danger fs-075 mt-1">
                        {balancesQuery.isError
                          ? getApiErrorMessage(
                              balancesQuery.error,
                              "Could not load balances.",
                            )
                          : `${employeeName(employee)} has no leave balance on ${formatPlainDate(asOf)} (not eligible yet, or no active policy).`}
                      </div>
                    )}
                </div>

                <div className="col-sm-6">
                  <label htmlFor="file-start" className="profile-label">
                    Start date <span className="text-danger">*</span>
                  </label>
                  <input
                    id="file-start"
                    type="date"
                    className="form-control"
                    value={form.startDate}
                    onChange={(e) => setDates({ startDate: e.target.value })}
                  />
                </div>
                <div className="col-sm-6">
                  <label htmlFor="file-end" className="profile-label">
                    End date <span className="text-danger">*</span>
                  </label>
                  <input
                    id="file-end"
                    type="date"
                    className="form-control"
                    value={form.endDate}
                    min={form.startDate || undefined}
                    onChange={(e) => setDates({ endDate: e.target.value })}
                  />
                </div>

                <div className="col-sm-6">
                  <div className="form-check form-switch mt-sm-4">
                    <input
                      id="file-half"
                      type="checkbox"
                      className="form-check-input"
                      checked={form.halfDay}
                      disabled={!isSingleDay}
                      onChange={(e) =>
                        set({ halfDay: e.target.checked, totalDays: "" })
                      }
                    />
                    <label
                      htmlFor="file-half"
                      className="form-check-label fs-085"
                    >
                      Half day{" "}
                      {!isSingleDay && (
                        <span className="text-slate-400">
                          (single-day leave only)
                        </span>
                      )}
                    </label>
                  </div>
                </div>
                <div className="col-sm-6">
                  <label htmlFor="file-days" className="profile-label">
                    Total days <span className="text-danger">*</span>
                  </label>
                  <input
                    id="file-days"
                    type="number"
                    className="form-control"
                    step="0.5"
                    min="0.5"
                    max={calendarDays || undefined}
                    value={
                      form.totalDays === ""
                        ? calendarDays
                          ? totalDays
                          : ""
                        : form.totalDays
                    }
                    onChange={(e) => set({ totalDays: e.target.value })}
                  />
                  <div className="text-slate-500 fs-070 mt-1">
                    Calculated from the dates; adjust for rest days or half
                    days.
                  </div>
                </div>

                <div className="col-12">
                  <label htmlFor="file-reason" className="profile-label">
                    Reason <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id="file-reason"
                    className="form-control"
                    rows={3}
                    maxLength={REASON_MAX}
                    value={form.reason}
                    onChange={(e) => set({ reason: e.target.value })}
                  />
                  <div className="text-end text-slate-400 fs-070 mt-1">
                    {form.reason.length}/{REASON_MAX}
                  </div>
                </div>
              </div>
            </fieldset>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Link
                to="/leave/approvals"
                className="btn btn-outline-secondary profile-btn"
              >
                Cancel
              </Link>
              <button
                type="submit"
                className="btn btn-primary profile-btn"
                disabled={!employee || busy}
              >
                {busy && (
                  <span
                    className="spinner-border spinner-border-sm"
                    aria-hidden="true"
                  ></span>
                )}
                File leave
              </button>
            </div>
          </div>

          {/* Summary beside the form */}
          <aside className="leave-card leave-file-summary">
            <h6 className="leave-section-title">Summary</h6>
            {!employee ? (
              <p className="fs-085 text-slate-500 mb-0">
                Choose an employee to see their balances.
              </p>
            ) : (
              <>
                <div className="fw-semibold text-navy">
                  {employeeName(employee)}
                </div>
                <div className="text-slate-500 fs-080 mb-3">
                  {employee.employeeId}
                </div>
                {form.startDate && (
                  <p className="fs-085 mb-2">
                    {formatLeaveDates(form.startDate, form.endDate)} ·{" "}
                    <strong>{formatDays(totalDays)}</strong>
                  </p>
                )}
                {selectedBalance ? (
                  <div
                    className={`leave-available ${overBalance ? "is-negative" : ""}`}
                  >
                    <div className="approval-meta-label">
                      {typeName(selectedBalance)} available
                    </div>
                    <div className="leave-balance-value">{available}</div>
                    {selectedBalance.periodStart && (
                      <div className="fs-075">
                        Cycle {formatPlainDate(selectedBalance.periodStart)} –{" "}
                        {formatPlainDate(selectedBalance.periodEnd)}
                      </div>
                    )}
                    {totalDays > 0 && (
                      <div className="fs-080 mt-1">
                        {overBalance
                          ? `Not enough balance: ${formatDays(totalDays - available)} short.`
                          : `${formatDays(available - totalDays)} left after this leave.`}
                      </div>
                    )}
                  </div>
                ) : (
                  balances.length > 0 && (
                    <p className="fs-085 text-slate-500 mb-0">
                      Pick a leave type to check the balance.
                    </p>
                  )
                )}
              </>
            )}
          </aside>
        </form>
      </div>
    </Layout>
  );
};

export default FileLeavePage;
